/**
 * Flight Templates Admin Page
 * Manage flight templates for the organization.
 * Supports both Orthomosaic and DSP template types with full flight planning settings.
 */

import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button, Modal, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { LuPlus, LuPencil, LuTrash, LuCopy, LuTriangleAlert } from 'react-icons/lu';

import AdminView from '@components/layout/views/AdminView';
import { useAuth } from '@contexts/User.context';
import { useConfirm } from '@contexts/Confirm.context';
import {
	getFlightTemplates,
	createFlightTemplate,
	updateFlightTemplate,
	deleteFlightTemplate,
} from '@utils/api/forestryFlight';
import { getAvailableModels } from '@utils/api/models';
import {
	DRONE_MODELS,
	DSP_LENSES,
} from '@routes/userRoutes/tools/flightPlanner/constants/flightConstants';
import { FEATURE_FLAGS } from '@constants/featureFlags';

// ─── Styled Components ────────────────────────────────────────────────────────

const PageHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1.5rem;
`;

const Title = styled.h1`
	color: ${props => props.theme.colors.textColor};
	font-size: 1.5rem;
	margin: 0;
`;

const TemplateGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
	gap: 1rem;
`;

const TemplateCard = styled.div`
	background-color: ${props => props.theme.colors.cardBackground};
	border: 1px solid ${props => props.theme.colors.border};
	border-radius: 8px;
	padding: 1.25rem;
	position: relative;
	display: flex;
	flex-direction: column;
	min-height: 220px;
`;

const TemplateHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 0.75rem;
`;

const TemplateName = styled.h3`
	color: ${props => props.theme.colors.textColor};
	font-size: 1.1rem;
	margin: 0;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const TemplateActions = styled.div`
	display: flex;
	gap: 0.5rem;
	flex-shrink: 0;
`;

const ActionButton = styled.button`
	background: none;
	border: none;
	color: ${props => (props.$danger ? '#f87171' : props.theme.colors.textMuted)};
	cursor: pointer;
	padding: 0.3rem 0.4rem;
	border-radius: 4px;
	opacity: 0.6;
	transition: all 0.15s ease;
	display: inline-flex;
	align-items: center;

	&:hover {
		opacity: 1;
		color: ${props => (props.$danger ? '#f87171' : props.theme.colors.textColor)};
		background: ${props =>
			props.$danger ? 'rgba(248, 113, 113, 0.1)' : 'rgba(255, 255, 255, 0.08)'};
	}
`;

const TemplateDescription = styled.p`
	color: ${props => props.theme.colors.textMuted};
	font-size: 0.9rem;
	margin-bottom: 1rem;
	min-height: 1.4rem;
`;

const SettingsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.5rem;
	font-size: 0.85rem;
	margin-top: auto;
`;

const SettingItem = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0.25rem 0;
	border-bottom: 1px solid ${props => props.theme.colors.border};

	.label {
		color: ${props => props.theme.colors.textMuted};
	}

	.value {
		color: ${props => props.theme.colors.textColor};
		font-weight: 500;
	}
`;

const ScopeBadge = styled(Badge)`
	font-size: 0.7rem;
	font-weight: 500;
`;

const SectionTitle = styled.h2`
	color: ${props => props.theme.colors.textColor};
	font-size: 1rem;
	margin: 2rem 0 1rem;
	padding-bottom: 0.5rem;
	border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const EmptyState = styled.div`
	text-align: center;
	padding: 3rem;
	color: ${props => props.theme.colors.textMuted};
`;

const FormSectionHeader = styled.h5`
	font-size: 0.95rem;
	font-weight: 600;
	margin: 1.25rem 0 0.75rem;
	padding-bottom: 0.4rem;
	border-bottom: 1px solid ${props => props.theme.colors.border || '#dee2e6'};
	color: ${props => props.theme.colors.textColor || '#333'};
`;

// ─── Default form state ───────────────────────────────────────────────────────

const DEFAULT_ORTHO_FORM = {
	name: '',
	description: '',
	template_type: 'orthomosaic',
	scope: 'organization',
	drone_model: '',
	flight_height: 80,
	side_overlap: 70,
	front_overlap: 75,
	speed: 8,
	terrain_follow: false,
	smart_oblique: false,
	gimbal_pitch: -90,
	flight_angle: 0,
	require_municipality: true,
	ai_model_uuid: '',
	is_default: false,
};

const DEFAULT_DSP_FORM = {
	name: '',
	description: '',
	template_type: 'dsp',
	scope: 'organization',
	drone_model: 'dji-matrice-4-enterprise',
	require_municipality: true,
	ai_model_uuid: '',
	is_default: false,
	// DSP-specific fields (sent nested as dsp_settings)
	dsp_point_count: 10,
	dsp_lens: 'wide',
	dsp_plot_area: 15,
	dsp_speed: 5,
	dsp_gimbal_pitch: -90,
	dsp_terrain_follow: true,
	dsp_alignment_angle: 0,
	dsp_minimum_distance: 50,
	dsp_maximum_distance: 200,
	dsp_max_overlap: 20,
	dsp_stop_at_dsp: true,
	dsp_stop_duration: 3,
};

// ─── Component ────────────────────────────────────────────────────────────────

const FlightTemplates = () => {
	const { isOrgAdmin, roleAdmin } = useAuth();
	const { confirm } = useConfirm();
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [saving, setSaving] = useState(false);
	const [modalError, setModalError] = useState(null);
	const [availableAiModels, setAvailableAiModels] = useState([]);

	const [formData, setFormData] = useState(DEFAULT_ORTHO_FORM);

	const fetchTemplates = useCallback(async () => {
		try {
			setLoading(true);
			const data = await getFlightTemplates();
			setTemplates(data);
			setError(null);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	// Fetch AI models for the dropdown
	useEffect(() => {
		const fetchModels = async () => {
			try {
				const models = await getAvailableModels();
				setAvailableAiModels(
					models.map(m => ({
						uuid: m.model_uuid,
						name: m.description || m.model_uuid,
					}))
				);
			} catch (err) {
				console.warn('Failed to fetch AI models:', err);
			}
		};
		fetchModels();
	}, []);

	/**
	 * Build form state from an existing template (for edit/duplicate).
	 * Handles both ortho and DSP templates, including nested dsp_settings.
	 */
	const buildFormFromTemplate = (template, overrides = {}) => {
		const base = {
			name: template.name,
			description: template.description || '',
			template_type: template.template_type || 'orthomosaic',
			scope: template.scope || 'organization',
			drone_model: template.drone_model || '',
			require_municipality: template.require_municipality ?? true,
			ai_model_uuid: template.ai_model_uuid || '',
			is_default: template.is_default || false,
			...overrides,
		};

		if (base.template_type === 'dsp') {
			const dsp = template.dsp_settings || {};
			return {
				...base,
				dsp_point_count: dsp.automatic_settings?.point_count ?? dsp.point_count ?? 10,
				dsp_lens: dsp.lens || 'wide',
				dsp_plot_area: dsp.plot_area ?? 15,
				dsp_speed: dsp.speed ?? 5,
				dsp_gimbal_pitch: dsp.gimbal_pitch ?? -90,
				dsp_terrain_follow: dsp.terrain_follow ?? true,
				dsp_alignment_angle: dsp.alignment_angle ?? 0,
				dsp_minimum_distance: dsp.minimum_distance ?? 50,
				dsp_maximum_distance: dsp.maximum_distance ?? 200,
				dsp_max_overlap: dsp.max_overlap ?? 20,
				dsp_stop_at_dsp: dsp.stop_at_dsp ?? true,
				dsp_stop_duration: dsp.stop_duration ?? 3,
			};
		}

		// Orthomosaic — read from flight_settings if present, fall back to flat fields
		const fs = template.flight_settings || {};
		return {
			...base,
			flight_height: fs.flight_height ?? template.flight_height ?? 80,
			side_overlap: fs.side_overlap ?? template.side_overlap ?? 70,
			front_overlap: fs.front_overlap ?? template.front_overlap ?? 75,
			speed: fs.speed ?? template.speed ?? 8,
			terrain_follow: fs.terrain_follow ?? template.terrain_follow ?? false,
			smart_oblique: fs.smart_oblique ?? template.smart_oblique ?? false,
			gimbal_pitch: fs.gimbal_pitch ?? template.gimbal_pitch ?? -90,
			flight_angle: fs.flight_angle ?? template.flight_angle ?? 0,
		};
	};

	/**
	 * Build the API payload from local form state.
	 * Structures dsp_settings / flight_settings as nested objects for the API.
	 */
	const buildPayload = () => {
		const payload = {
			name: formData.name,
			description: formData.description,
			template_type: formData.template_type,
			scope: formData.scope,
			drone_model: formData.drone_model || null,
			require_municipality: formData.require_municipality,
			ai_model_uuid: formData.ai_model_uuid || null,
			is_default: formData.is_default,
		};

		if (formData.template_type === 'dsp') {
			payload.dsp_settings = {
				method: 'automatic',
				automatic_settings: {
					point_count: formData.dsp_point_count,
				},
				lens: formData.dsp_lens,
				plot_area: formData.dsp_plot_area,
				speed: formData.dsp_speed,
				gimbal_pitch: formData.dsp_gimbal_pitch,
				terrain_follow: formData.dsp_terrain_follow,
				alignment_angle: formData.dsp_alignment_angle,
				minimum_distance: formData.dsp_minimum_distance,
				maximum_distance: formData.dsp_maximum_distance,
				max_overlap: formData.dsp_max_overlap,
				stop_at_dsp: formData.dsp_stop_at_dsp,
				stop_duration: formData.dsp_stop_duration,
			};
		} else {
			// Orthomosaic: send both flat fields (backward compat) and structured object
			payload.flight_height = formData.flight_height;
			payload.side_overlap = formData.side_overlap;
			payload.front_overlap = formData.front_overlap;
			payload.speed = formData.speed;
			payload.terrain_follow = formData.terrain_follow;
			payload.smart_oblique = formData.smart_oblique;
			payload.gimbal_pitch = formData.gimbal_pitch;
			payload.flight_angle = formData.flight_angle;
			payload.flight_settings = {
				flight_height: formData.flight_height,
				side_overlap: formData.side_overlap,
				front_overlap: formData.front_overlap,
				speed: formData.speed,
				gimbal_pitch: formData.gimbal_pitch,
				terrain_follow: formData.terrain_follow,
				smart_oblique: formData.smart_oblique,
				flight_angle: formData.flight_angle,
			};
		}

		return payload;
	};

	const handleOpenModal = (template = null) => {
		setModalError(null);
		if (template) {
			setEditingTemplate(template);
			setFormData(buildFormFromTemplate(template));
		} else {
			setEditingTemplate(null);
			setFormData(DEFAULT_ORTHO_FORM);
		}
		setShowModal(true);
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setEditingTemplate(null);
		setModalError(null);
	};

	const handleTemplateTypeChange = newType => {
		if (newType === 'dsp') {
			setFormData(prev => ({
				...DEFAULT_DSP_FORM,
				name: prev.name,
				description: prev.description,
				scope: prev.scope,
				drone_model: 'dji-matrice-4-enterprise',
				require_municipality: prev.require_municipality,
				ai_model_uuid: prev.ai_model_uuid,
			}));
		} else {
			setFormData(prev => ({
				...DEFAULT_ORTHO_FORM,
				name: prev.name,
				description: prev.description,
				scope: prev.scope,
				drone_model: prev.drone_model,
				require_municipality: prev.require_municipality,
				ai_model_uuid: prev.ai_model_uuid,
			}));
		}
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			setModalError(null);
			const payload = buildPayload();
			if (editingTemplate) {
				await updateFlightTemplate(editingTemplate.id, payload);
			} else {
				await createFlightTemplate(payload);
			}
			await fetchTemplates();
			handleCloseModal();
		} catch (err) {
			setModalError(err.response?.data?.detail || err.message || 'Failed to save template');
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async templateId => {
		const userConfirmed = await confirm({
			title: 'Delete Template',
			message: 'Are you sure you want to delete this template?',
		});
		if (!userConfirmed) return;
		try {
			await deleteFlightTemplate(templateId);
			await fetchTemplates();
		} catch (err) {
			setError(err.response?.data?.detail || err.message || 'Failed to delete template');
		}
	};

	const handleDuplicate = template => {
		setEditingTemplate(null);
		setFormData(
			buildFormFromTemplate(template, {
				name: `${template.name} (Copy)`,
				scope: 'organization',
				is_default: false,
			})
		);
		setShowModal(true);
	};

	const systemTemplates = templates.filter(t => t.scope === 'system');
	const orgTemplates = templates.filter(t => t.scope === 'organization');
	const userTemplates = templates.filter(t => t.scope === 'user');

	const canEdit = isOrgAdmin || roleAdmin;

	// ─── Template Card ────────────────────────────────────────────────────────

	const renderTemplateCard = template => {
		const isDsp = template.template_type === 'dsp';
		const dsp = template.dsp_settings || {};

		return (
			<TemplateCard key={template.id}>
				<TemplateHeader>
					<TemplateName>
						{template.name}
						{template.is_default && <ScopeBadge bg="success">Default</ScopeBadge>}
						<ScopeBadge bg={isDsp ? 'info' : 'secondary'}>
							{isDsp ? 'DSP' : 'Ortho'}
						</ScopeBadge>
					</TemplateName>
					<TemplateActions>
						<ActionButton onClick={() => handleDuplicate(template)} title="Duplicate">
							<LuCopy />
						</ActionButton>
						{template.scope !== 'system' && canEdit && (
							<>
								<ActionButton
									onClick={() => handleOpenModal(template)}
									title="Edit">
									<LuPencil />
								</ActionButton>
								<ActionButton
									$danger
									onClick={() => handleDelete(template.id)}
									title="Delete">
									<LuTrash />
								</ActionButton>
							</>
						)}
					</TemplateActions>
				</TemplateHeader>

				<TemplateDescription>
					{template.description || 'No description'}
				</TemplateDescription>

				{isDsp ? (
					<SettingsGrid>
						<SettingItem>
							<span className="label">DSPs</span>
							<span className="value">
								{dsp.automatic_settings?.point_count || dsp.point_count || '—'}
							</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Plot Area</span>
							<span className="value">{dsp.plot_area || '—'} m²</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Lens</span>
							<span className="value">
								{DSP_LENSES[dsp.lens]?.name || dsp.lens || '—'}
							</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Speed</span>
							<span className="value">{dsp.speed || '—'} m/s</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Terrain Follow</span>
							<span className="value">{dsp.terrain_follow ? 'Yes' : 'No'}</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Stop at DSP</span>
							<span className="value">{dsp.stop_at_dsp ? 'Yes' : 'No'}</span>
						</SettingItem>
					</SettingsGrid>
				) : (
					<SettingsGrid>
						<SettingItem>
							<span className="label">Height</span>
							<span className="value">{template.flight_height}m</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Speed</span>
							<span className="value">{template.speed} m/s</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Side Overlap</span>
							<span className="value">{template.side_overlap}%</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Front Overlap</span>
							<span className="value">{template.front_overlap}%</span>
						</SettingItem>
						<SettingItem>
							<span className="label">Terrain Follow</span>
							<span className="value">{template.terrain_follow ? 'Yes' : 'No'}</span>
						</SettingItem>
						{FEATURE_FLAGS.SMART_OBLIQUE_ENABLED && (
							<SettingItem>
								<span className="label">Smart Oblique</span>
								<span className="value">
									{template.smart_oblique ? 'Yes' : 'No'}
								</span>
							</SettingItem>
						)}
					</SettingsGrid>
				)}
			</TemplateCard>
		);
	};

	// ─── Ortho Settings Form Section ──────────────────────────────────────────

	const renderOrthoSettings = () => (
		<>
			<FormSectionHeader>Flight Settings</FormSectionHeader>
			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Flight Height (m)</Form.Label>
						<Form.Control
							type="number"
							min={20}
							max={FEATURE_FLAGS.MAX_FLIGHT_HEIGHT}
							value={formData.flight_height}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									flight_height: parseInt(e.target.value),
								}))
							}
						/>
						<Form.Text muted>
							20–{FEATURE_FLAGS.MAX_FLIGHT_HEIGHT}m AGL
							{formData.flight_height >
								FEATURE_FLAGS.AUTHORIZATION_HEIGHT_THRESHOLD && (
								<>
									{' '}
									(<LuTriangleAlert /> {'>'}
									{FEATURE_FLAGS.AUTHORIZATION_HEIGHT_THRESHOLD}m requires
									authorization)
								</>
							)}
						</Form.Text>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Speed (m/s)</Form.Label>
						<Form.Control
							type="number"
							min={2}
							max={15}
							value={formData.speed}
							onChange={e =>
								setFormData(prev => ({ ...prev, speed: parseInt(e.target.value) }))
							}
						/>
						<Form.Text muted>2–15 m/s</Form.Text>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Side Overlap (%)</Form.Label>
						<Form.Control
							type="number"
							min={50}
							max={95}
							value={formData.side_overlap}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									side_overlap: parseInt(e.target.value),
								}))
							}
						/>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Front Overlap (%)</Form.Label>
						<Form.Control
							type="number"
							min={50}
							max={95}
							value={formData.front_overlap}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									front_overlap: parseInt(e.target.value),
								}))
							}
						/>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Gimbal Pitch (°)</Form.Label>
						<Form.Control
							type="number"
							min={-90}
							max={0}
							value={formData.gimbal_pitch}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									gimbal_pitch: parseInt(e.target.value),
								}))
							}
						/>
						<Form.Text muted>-90° = nadir (straight down)</Form.Text>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Flight Angle (°)</Form.Label>
						<Form.Control
							type="number"
							min={0}
							max={180}
							value={formData.flight_angle}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									flight_angle: parseInt(e.target.value),
								}))
							}
						/>
						<Form.Text muted>0° = N-S, 90° = E-W</Form.Text>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-4">
					<Form.Check
						type="switch"
						id="terrain-follow"
						label="Terrain Following"
						checked={formData.terrain_follow}
						onChange={e =>
							setFormData(prev => ({ ...prev, terrain_follow: e.target.checked }))
						}
					/>
				</div>
				{FEATURE_FLAGS.SMART_OBLIQUE_ENABLED && (
					<div className="col-md-4">
						<Form.Check
							type="switch"
							id="smart-oblique"
							label="Smart Oblique"
							checked={formData.smart_oblique}
							onChange={e =>
								setFormData(prev => ({ ...prev, smart_oblique: e.target.checked }))
							}
						/>
					</div>
				)}
			</div>
		</>
	);

	// ─── DSP Settings Form Section ────────────────────────────────────────────

	const renderDspSettings = () => (
		<>
			<FormSectionHeader>DSP Settings</FormSectionHeader>
			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Number of DSPs</Form.Label>
						<Form.Control
							type="number"
							min={1}
							max={5000}
							value={formData.dsp_point_count}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_point_count: parseInt(e.target.value) || 1,
								}))
							}
						/>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Lens</Form.Label>
						<Form.Select
							value={formData.dsp_lens}
							onChange={e =>
								setFormData(prev => ({ ...prev, dsp_lens: e.target.value }))
							}>
							{Object.entries(DSP_LENSES).map(([id, lens]) => (
								<option key={id} value={id}>
									{lens.name}
								</option>
							))}
						</Form.Select>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Plot Area (m²)</Form.Label>
						<Form.Control
							type="number"
							min={1}
							max={500}
							value={formData.dsp_plot_area}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_plot_area: parseInt(e.target.value) || 1,
								}))
							}
						/>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Speed (m/s)</Form.Label>
						<Form.Control
							type="number"
							min={2}
							max={15}
							value={formData.dsp_speed}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_speed: parseInt(e.target.value),
								}))
							}
						/>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Gimbal Pitch (°)</Form.Label>
						<Form.Control
							type="number"
							min={-90}
							max={0}
							value={formData.dsp_gimbal_pitch}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_gimbal_pitch: parseInt(e.target.value),
								}))
							}
						/>
						<Form.Text muted>-90° = straight down (nadir)</Form.Text>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Alignment Angle (°)</Form.Label>
						<Form.Control
							type="number"
							min={0}
							max={180}
							step={15}
							value={formData.dsp_alignment_angle}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_alignment_angle: parseInt(e.target.value),
								}))
							}
						/>
						<Form.Text muted>0° = N-S, 90° = E-W</Form.Text>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Min Distance (m)</Form.Label>
						<Form.Control
							type="number"
							min={10}
							max={500}
							value={formData.dsp_minimum_distance}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_minimum_distance: parseInt(e.target.value),
								}))
							}
						/>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Max Distance (m)</Form.Label>
						<Form.Control
							type="number"
							min={10}
							max={1000}
							value={formData.dsp_maximum_distance}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_maximum_distance: parseInt(e.target.value),
								}))
							}
						/>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Overlap / Gap (%)</Form.Label>
						<Form.Control
							type="number"
							min={-50}
							max={50}
							value={formData.dsp_max_overlap}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_max_overlap: parseInt(e.target.value),
								}))
							}
						/>
						<Form.Text muted>
							{formData.dsp_max_overlap >= 0
								? `${formData.dsp_max_overlap}% overlap between images`
								: `${Math.abs(formData.dsp_max_overlap)}% gap between images`}
						</Form.Text>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Stop Duration (s)</Form.Label>
						<Form.Control
							type="number"
							min={1}
							max={60}
							value={formData.dsp_stop_duration}
							disabled={!formData.dsp_stop_at_dsp}
							onChange={e =>
								setFormData(prev => ({
									...prev,
									dsp_stop_duration: parseInt(e.target.value),
								}))
							}
						/>
						<Form.Text muted>Hover time at each DSP point</Form.Text>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-4">
					<Form.Check
						type="switch"
						id="dsp-terrain-follow"
						label="Terrain Following"
						checked={formData.dsp_terrain_follow}
						onChange={e =>
							setFormData(prev => ({ ...prev, dsp_terrain_follow: e.target.checked }))
						}
					/>
				</div>
				<div className="col-md-4">
					<Form.Check
						type="switch"
						id="dsp-stop-at-dsp"
						label="Stop at Each DSP"
						checked={formData.dsp_stop_at_dsp}
						onChange={e =>
							setFormData(prev => ({ ...prev, dsp_stop_at_dsp: e.target.checked }))
						}
					/>
				</div>
			</div>
		</>
	);

	// ─── Render ───────────────────────────────────────────────────────────────

	if (loading) {
		return (
			<AdminView>
				<div style={{ textAlign: 'center', padding: '3rem' }}>
					<Spinner animation="border" />
				</div>
			</AdminView>
		);
	}

	return (
		<AdminView>
			<PageHeader>
				<Title>Flight Templates</Title>
				{canEdit && (
					<Button variant="success" onClick={() => handleOpenModal()}>
						<LuPlus /> New Template
					</Button>
				)}
			</PageHeader>

			{error && (
				<Alert variant="danger" dismissible onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			<SectionTitle>System Templates</SectionTitle>
			{systemTemplates.length > 0 ? (
				<TemplateGrid>{systemTemplates.map(renderTemplateCard)}</TemplateGrid>
			) : (
				<EmptyState>No system templates available</EmptyState>
			)}

			<SectionTitle>Organization Templates</SectionTitle>
			{orgTemplates.length > 0 ? (
				<TemplateGrid>{orgTemplates.map(renderTemplateCard)}</TemplateGrid>
			) : (
				<EmptyState>
					No organization templates yet.
					{canEdit && ' Click "New Template" to create one.'}
				</EmptyState>
			)}

			{userTemplates.length > 0 && (
				<>
					<SectionTitle>Personal Templates</SectionTitle>
					<TemplateGrid>{userTemplates.map(renderTemplateCard)}</TemplateGrid>
				</>
			)}

			{/* Create/Edit Modal */}
			<Modal show={showModal} onHide={handleCloseModal} size="lg">
				<Modal.Header closeButton>
					<Modal.Title>
						{editingTemplate ? 'Edit Template' : 'Create New Template'}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{modalError && (
						<Alert variant="danger" dismissible onClose={() => setModalError(null)}>
							{modalError}
						</Alert>
					)}
					<Form>
						{/* ── General Info ── */}
						<Form.Group className="mb-3">
							<Form.Label>Template Name</Form.Label>
							<Form.Control
								type="text"
								value={formData.name}
								onChange={e =>
									setFormData(prev => ({ ...prev, name: e.target.value }))
								}
								placeholder="Enter template name"
							/>
						</Form.Group>

						<Form.Group className="mb-3">
							<Form.Label>Description</Form.Label>
							<Form.Control
								as="textarea"
								rows={2}
								value={formData.description}
								onChange={e =>
									setFormData(prev => ({ ...prev, description: e.target.value }))
								}
								placeholder="Brief description of this template"
							/>
						</Form.Group>

						<div className="row">
							<div className="col-md-4">
								<Form.Group className="mb-3">
									<Form.Label>Template Type</Form.Label>
									<Form.Select
										value={formData.template_type}
										onChange={e => handleTemplateTypeChange(e.target.value)}>
										<option value="orthomosaic">Orthomosaic</option>
										<option value="dsp">DSP (Digital Sample Plot)</option>
									</Form.Select>
								</Form.Group>
							</div>
							<div className="col-md-4">
								<Form.Group className="mb-3">
									<Form.Label>Scope</Form.Label>
									<Form.Select
										value={formData.scope}
										onChange={e =>
											setFormData(prev => ({
												...prev,
												scope: e.target.value,
											}))
										}>
										<option value="organization">Organization</option>
										<option value="user">Personal</option>
									</Form.Select>
								</Form.Group>
							</div>
							<div className="col-md-4">
								<Form.Group className="mb-3">
									<Form.Label>Drone Model</Form.Label>
									<Form.Select
										value={formData.drone_model}
										disabled={formData.template_type === 'dsp'}
										onChange={e =>
											setFormData(prev => ({
												...prev,
												drone_model: e.target.value,
											}))
										}>
										<option value="">None (any drone)</option>
										{DRONE_MODELS.map(drone => (
											<option key={drone.id} value={drone.id}>
												{drone.name}
											</option>
										))}
									</Form.Select>
									{formData.template_type === 'dsp' && (
										<Form.Text muted>
											DSP requires Matrice 4 Enterprise
										</Form.Text>
									)}
								</Form.Group>
							</div>
						</div>

						{/* ── Type-specific settings ── */}
						{formData.template_type === 'dsp'
							? renderDspSettings()
							: renderOrthoSettings()}

						{/* ── Common options ── */}
						<FormSectionHeader>Additional Options</FormSectionHeader>
						<div className="row">
							<div className="col-md-6">
								<Form.Group className="mb-3">
									<Form.Label>AI Model</Form.Label>
									<Form.Select
										value={formData.ai_model_uuid}
										onChange={e =>
											setFormData(prev => ({
												...prev,
												ai_model_uuid: e.target.value,
											}))
										}>
										<option value="">None</option>
										{availableAiModels.map(model => (
											<option key={model.uuid} value={model.uuid}>
												{model.name}
											</option>
										))}
									</Form.Select>
									<Form.Text muted>Auto-run after processing</Form.Text>
								</Form.Group>
							</div>
						</div>

						<div className="row">
							<div className="col-md-4">
								<Form.Check
									type="switch"
									id="require-municipality"
									label="Require Municipality"
									checked={formData.require_municipality}
									onChange={e =>
										setFormData(prev => ({
											...prev,
											require_municipality: e.target.checked,
										}))
									}
								/>
							</div>
							<div className="col-md-4">
								<Form.Check
									type="switch"
									id="is-default"
									label="Set as Default"
									checked={formData.is_default}
									onChange={e =>
										setFormData(prev => ({
											...prev,
											is_default: e.target.checked,
										}))
									}
								/>
							</div>
						</div>
					</Form>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={handleCloseModal}>
						Cancel
					</Button>
					<Button
						variant="success"
						onClick={handleSave}
						disabled={saving || !formData.name}>
						{saving ? (
							<Spinner size="sm" />
						) : editingTemplate ? (
							'Save Changes'
						) : (
							'Create Template'
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</AdminView>
	);
};

export default FlightTemplates;
